import { getRequest, postRequest, putRequest, patchRequest, deleteRequest } from './http.service';
import {
  URL_CONNECTOR_USER_ZOOM_STATUS,
  URL_CONNECTOR_USER_ZOOM_DISCONNECT,
  URL_CONNECTOR_USER_CALENDLY_DISCONNECT,
  URL_CONNECTOR_USER_DELETE,
  URL_CONNECTOR_TOGGLE_EMAIL,
  URL_CONNECTOR_TOGGLE_AUTO_RECORDING,
  URL_CONNECTOR_TOGGLE_OUTLOOK,
  URL_CONNECTOR_TOGGLE_STATUS,
  URL_CONNECTOR_USER,
  URL_CONNECTORS,
  URL_CONNECTOR_BY_ID,
  URL_CONNECTOR_SHARING,
  URL_CONNECTOR_BY_ORG,
  URL_CONNECTOR_STORAGE_STATS,
  URL_CONNECTOR_SHAREPOINT_DRIVES,
  URL_CONNECTOR_SHAREPOINT_DRIVE_LOC,
  URL_CONNECTOR_MIGRATION,
  URL_MICROSOFT_ORG_DISCONNECT,
  URL_MICROSOFT_ORG_OAUTH_URL,
  URL_INTELLIFLO_CREDENTIALS,
  URL_OUTLOOK_CONTACTS_STATUS,
  URL_OUTLOOK_CONTACTS_OAUTH,
  URL_OUTLOOK_CONTACTS_DISCONNECT,
  URL_FIREFLIES_CREDENTIALS,
  URL_ADVISER_LOGIC_CREDENTIALS,
  URL_ZOHO_CALLBACK,
  URL_ZOHO_STATUS,
  URL_ZOHO_CONTACTS,
  URL_ZOHO_CONNECT,
  URL_MONDAY_IMPORT_ALL,
  URL_WORKSORTED_COMPANIES,
  URL_WORKSORTED_CLIENTS,
  URL_ADVISER_LOGIC_CONTACTS,
  URL_ADVISER_LOGIC_STATUS,
  URL_OUTLOOK_CONTACTS,
  URL_INTELLIFLO_CONTACTS,
  URL_INTELLIFLO_STATUS,
  URL_CONNECTOR_STATUS,
} from './url.service';

export const checkZoomStatus = (userId: string) =>
  getRequest(URL_CONNECTOR_USER_ZOOM_STATUS(userId));

export const toggleAutoRecording = (payload: Record<string, unknown>) =>
  patchRequest(URL_CONNECTOR_TOGGLE_AUTO_RECORDING, payload);

export const fetchMsStatus = () =>
  getRequest(URL_OUTLOOK_CONTACTS_STATUS);

export const getMsOAuthUrl = (redirectUrl: string) =>
  getRequest(URL_OUTLOOK_CONTACTS_OAUTH(redirectUrl));

export const disconnectMs = () =>
  postRequest(URL_OUTLOOK_CONTACTS_DISCONNECT, {});

export const fetchUserConnectors = (userId: string) =>
  getRequest(URL_CONNECTOR_USER(userId));

export const toggleConnectorStatus = (payload: Record<string, unknown>) =>
  patchRequest(URL_CONNECTOR_TOGGLE_STATUS, payload);

export const saveFirefliesCredentials = (apiKey: string) =>
  postRequest(URL_FIREFLIES_CREDENTIALS, { apiKey });

export const saveAdviserLogicCredentials = (payload: Record<string, unknown>) =>
  postRequest(URL_ADVISER_LOGIC_CREDENTIALS, payload);

export const disconnectZoom = (userId: string) =>
  deleteRequest(URL_CONNECTOR_USER_ZOOM_DISCONNECT(userId));

export const disconnectCalendly = (userId: string) =>
  deleteRequest(URL_CONNECTOR_USER_CALENDLY_DISCONNECT(userId));

// Generic OAuth connector disconnect by its stored connector _id.
export const deleteConnectorById = (connectorId: string) =>
  deleteRequest(URL_CONNECTOR_USER_DELETE(connectorId));

export const updateConnector = (connectorId: string, payload: Record<string, unknown>) =>
  putRequest(URL_CONNECTOR_BY_ID(connectorId), payload);

export const updateConnectorSharing = (
  connectorId: string,
  sharedWithOrganisationIds: string[],
) => putRequest(URL_CONNECTOR_SHARING(connectorId), { sharedWithOrganisationIds });

export const createConnector = (payload: Record<string, unknown>) =>
  postRequest(URL_CONNECTORS, payload);

export const fetchConnectorByOrg = (orgId: string) =>
  getRequest(URL_CONNECTOR_BY_ORG(orgId));


// ---------------------------------------------------------------------------
// Org-level connector toggles
// ---------------------------------------------------------------------------

export const toggleEmailStatus = (orgId: string) =>
  patchRequest(URL_CONNECTOR_TOGGLE_EMAIL, { orgId });

export const toggleOutlookContactsStatus = (orgId: string) =>
  patchRequest(URL_CONNECTOR_TOGGLE_OUTLOOK, { orgId });

// ---------------------------------------------------------------------------
// Microsoft 365 org-level OAuth
// ---------------------------------------------------------------------------

export const getMicrosoft365OAuthUrl = (redirectUrl: string) =>
  getRequest(URL_MICROSOFT_ORG_OAUTH_URL(redirectUrl));

export const disconnectMicrosoft365Org = () =>
  postRequest(URL_MICROSOFT_ORG_DISCONNECT, {});

// ---------------------------------------------------------------------------
// Intelliflo
// ---------------------------------------------------------------------------

export const saveIntellifloCredentials = (payload: Record<string, unknown>) =>
  postRequest(URL_INTELLIFLO_CREDENTIALS, payload);

// ---------------------------------------------------------------------------
// Storage & SharePoint
// ---------------------------------------------------------------------------

export const fetchOrgStorageStats = (orgId: string) =>
  getRequest(URL_CONNECTOR_STORAGE_STATS(orgId));

export const fetchSharePointDrives = (orgId: string) =>
  getRequest(URL_CONNECTOR_SHAREPOINT_DRIVES(orgId));

export const updateSharePointDriveLocation = (
  orgId: string,
  payload: { sharepointDriveId: string; sharepointSiteName: string; sharepointWebUrl: string },
) => putRequest(URL_CONNECTOR_SHAREPOINT_DRIVE_LOC(orgId), payload as Record<string, unknown>);

export const startMigration = (orgId: string, targetProvider: string) =>
  postRequest(URL_CONNECTOR_MIGRATION(orgId, 'start'), { targetProvider });

export const pauseMigration = (orgId: string) =>
  postRequest(URL_CONNECTOR_MIGRATION(orgId, 'pause'), {});

export const resumeMigration = (orgId: string) =>
  postRequest(URL_CONNECTOR_MIGRATION(orgId, 'resume'), {});

export const handleZohoCallback = (payload: Record<string, unknown>) =>
  postRequest(URL_ZOHO_CALLBACK, payload);

// ---------------------------------------------------------------------------
// Third-party CRM contact import connectors
// ---------------------------------------------------------------------------

/** Get the generic connector enabled/disabled status for an org + source */
export const fetchConnectorStatus = (orgId: string, sourceName: string) =>
  getRequest(URL_CONNECTOR_STATUS(orgId, sourceName));

export const getZohoConnectUrl = (redirectUrl: string) =>
  getRequest(URL_ZOHO_CONNECT(redirectUrl));

export const fetchZohoStatus = () =>
  getRequest(URL_ZOHO_STATUS);

export const fetchZohoContacts = () =>
  getRequest(URL_ZOHO_CONTACTS);

export const importAllMondayContacts = () =>
  getRequest(URL_MONDAY_IMPORT_ALL);

export const fetchWorksortedCompanies = () =>
  getRequest(URL_WORKSORTED_COMPANIES);

export const fetchWorksortedClients = (companyId: string, count = 100) =>
  getRequest(`${URL_WORKSORTED_CLIENTS(companyId)}?count=${count}`);

export const fetchAdviserLogicContacts = () =>
  getRequest(URL_ADVISER_LOGIC_CONTACTS);

export const fetchAdviserLogicStatus = () =>
  getRequest(URL_ADVISER_LOGIC_STATUS);

export const fetchOutlookContacts = () =>
  getRequest(URL_OUTLOOK_CONTACTS);

export const fetchIntellifloContacts = () =>
  getRequest(URL_INTELLIFLO_CONTACTS);

export const fetchIntellifloStatus = () =>
  getRequest(URL_INTELLIFLO_STATUS);
